// client/src/App.jsx
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";

import { useEffect, lazy, Suspense } from "react";
import MemoryChallengeVideo from "./pages/public/MemoryChallengeVideo";

import Navbar from "./components/Navbar";
import { AuthProvider } from "./context/AuthContext";
import SmartSignup from "./pages/student/SmartSignup";
import Activation from "./pages/student/Activation";
import FreeLiveClass from "./pages/student/FreeLiveClass";
import QuickStart from "./pages/student/QuickStart";
import StartPractice from "./pages/student/StartPractice";

import Dashboard from "./pages/student/Dashboard.jsx";
import Leaderboard from "./pages/student/Leaderboard.jsx";
import LessonList from "./pages/student/LessonList";
import LessonDetail from "./pages/student/LessonDetail.jsx";
import LessonQuiz from "./pages/student/LessonQuiz.jsx";
import Practice from "./pages/student/Practice.jsx";
import TypingQuiz from "./pages/student/TypingQuiz.jsx";
import SentencePractice from "./pages/student/SentencePractice.jsx";
import Home from "./pages/Home";
import Login from "./pages/student/Login.jsx";
import JoinWebinar from "./pages/JoinWebinar";
import LevelCheck from "./pages/student/LevelCheck";
import AdminLessons from "./pages/admin/AdminLessons.jsx";
import Signup from "./pages/student/Signup.jsx";
import SpokenEnglishVSL from "./pages/marketing/SpokenEnglishVSL";
import SpokenEnglishOffer from "./pages/marketing/SpokenEnglishOffer";
import SpokenEnglishOfferV2 from "./pages/marketing/SpokenEnglishOfferV2";
import SpokenEnglishChallenge from "./pages/public/SpokenEnglishChallenge";
import MemoryChallenge from "./pages/public/MemoryChallenge";
import MemoryMasterclassParentsAB from "./pages/public/MemoryMasterclassParentsAB";
import MemoryChallengeStart from "./pages/public/MemoryChallengeStart";
import MemoryChallengeTest from "./pages/public/MemoryChallengeTest";
import MemoryChallengeResult from "./pages/public/MemoryChallengeResult";
import TrySpokenEnglishGym from "./pages/marketing/TrySpokenEnglishGym";
import VocabularyVSL from "./pages/marketing/VocabularyVSL";
import VocabularyCourse from "./pages/marketing/VocabularyCourse";
import VocabularyThankYou from "./pages/marketing/VocabularyThankYou";
import MemoryMasterclassThankYou from "./pages/public/MemoryMasterclassThankYou";
import WebinarAttend from "./pages/student/WebinarAttend";
import WebinarPreviewComplete from "./pages/student/WebinarAttend";

// Route guards
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute.jsx";
import RouteTracker from "./components/RouteTracker";
import FunnelDocumentBoundary from "./components/FunnelDocumentBoundary";
import MemoryMetaConsent from "./components/memory/MemoryMetaConsent";
import Webinar from "./pages/Webinar";
import SpokenEnglishThankYou from "./pages/marketing/SpokenEnglishThankYou";
import GermanLiveClass from "./pages/GermanLiveClass";
import GermanLiveThankYou from "./pages/GermanLiveThankYou";

function PageLoader() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="rounded-3xl bg-white px-8 py-7 shadow-sm border border-slate-100 text-center">
        <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600" />

        <p className="text-base font-extrabold text-slate-900">
          Loading FluencyJet...
        </p>

        <p className="mt-1 text-sm font-medium text-slate-500">
          Please wait a moment
        </p>
      </div>
    </div>
  );
}

function MemoryRouteNotFound() {
  return (
    <main id="memory-route-not-found" className="mx-auto max-w-xl px-6 py-16 text-center">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <a className="mt-6 inline-block font-bold text-indigo-700" href="/memory-challenge">
        Return to Memory Challenge
      </a>
    </main>
  );
}

// Student pages - lazy loaded
// const Home = lazy(() => import("./pages/Home"));
// const Webinar = lazy(() => import("./pages/Webinar"));
// const Dashboard = lazy(() => import("./pages/student/Dashboard.jsx"));
// const LessonDetail = lazy(() => import("./pages/student/LessonDetail.jsx"));
// const LessonQuiz = lazy(() => import("./pages/student/LessonQuiz.jsx"));
// const Leaderboard = lazy(() => import("./pages/student/Leaderboard.jsx"));
// const Login = lazy(() => import("./pages/student/Login.jsx"));
// const Signup = lazy(() => import("./pages/student/Signup.jsx"));
// const Practice = lazy(() => import("./pages/student/Practice.jsx"));
// const TypingQuiz = lazy(() => import("./pages/student/TypingQuiz.jsx"));
// const SentencePractice = lazy(
//  () => import("./pages/student/SentencePractice.jsx"),
// );
const Paywall = lazy(() => import("./pages/student/Paywall.jsx"));
const Checkout = lazy(() => import("./pages/student/Checkout.jsx"));
// const LevelCheck = lazy(() => import("./pages/student/LevelCheck"));
const Upgrade = lazy(() => import("./pages/student/Upgrade"));
// const LessonList = lazy(() => import("./pages/student/LessonList"));
const Profile = lazy(() => import("./pages/student/Profile"));
const Settings = lazy(() => import("./pages/student/Settings"));
const ForgotPassword = lazy(() => import("./pages/student/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/student/ResetPassword"));

// Admin pages - lazy loaded
const Admin = lazy(() => import("./pages/admin/Admin.jsx"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.jsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.jsx"));
// const AdminLessons = lazy(() => import("./pages/admin/AdminLessons.jsx"));
const AdminLessonCreate = lazy(
  () => import("./pages/admin/AdminLessonCreate.jsx"),
);
const AdminLessonEdit = lazy(() => import("./pages/admin/AdminLessonEdit.jsx"));
const AdminQuizzes = lazy(() => import("./pages/admin/AdminQuizzes.jsx"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers.jsx"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail.jsx"));
const AdminXP = lazy(() => import("./pages/admin/AdminXP.jsx"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics.jsx"));
const WhatsAppBroadcast = lazy(() => import("./pages/admin/WhatsAppBroadcast.jsx"));
const CurriculumManager = lazy(() => import("./pages/admin/CurriculumManager"));

// Legal pages - lazy loaded
const About = lazy(() => import("./pages/legal/About"));
const Contact = lazy(() => import("./pages/legal/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));

function MainLayout() {
  const location = useLocation();

  const hideNavbarPaths = [
    "/level-check",
    "/smart-signup",
    "/activation",
    "/quick-start",
    "/start-practice",
    "/webinar",
    "/attend-webinar",
    "/webinar-preview-complete",
  ];

  const shouldHideNavbar = hideNavbarPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(path + "/"),
  );

  return (
    <>
      {!shouldHideNavbar && <Navbar />}
      <main className="app-main">
        <Outlet />
      </main>
    </>
  );
}

const TRACK_KEY = "fj_track";

function inferTrackFromPath(pathname) {
  if (pathname.startsWith("/b/")) return "beginner";
  if (pathname.startsWith("/i/")) return "intermediate";
  return null;
}

function writeTrackToStorage(track) {
  try {
    if (track === "beginner" || track === "intermediate") {
      localStorage.setItem(TRACK_KEY, track);
    }
  } catch {}
}

function TrackSync() {
  const location = useLocation();

  useEffect(() => {
    const inferred = inferTrackFromPath(location.pathname);
    if (inferred) writeTrackToStorage(inferred);
  }, [location.pathname]);

  return null;
}

function readTrackFromStorage() {
  // 1) Primary: fj_track set by /level-check
  try {
    const t = localStorage.getItem(TRACK_KEY);
    if (t === "intermediate" || t === "beginner") return t;
  } catch {}

  // 2) Secondary: user.track (if you store it)
  try {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    const t = String(u?.track || "").toLowerCase();
    if (t === "intermediate" || t === "beginner") return t;
  } catch {}

  // 3) Fallback: old plan logic
  try {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    const plan = String(u?.plan || "").toLowerCase();
    if (plan.includes("inter")) return "intermediate";
  } catch {}

  return "beginner";
}

function LessonsRedirect() {
  const track = readTrackFromStorage();
  const target = track === "intermediate" ? "/i/lessons" : "/b/lessons";
  return <Navigate to={target} replace />;
}

function LessonRedirect() {
  const { lessonId } = useParams();

  const track = readTrackFromStorage();
  const base = track === "intermediate" ? "/i" : "/b";
  const diff = track === "intermediate" ? "intermediate" : "beginner";

  return (
    <Navigate
      to={`${base}/lesson/${encodeURIComponent(
        lessonId,
      )}?difficulty=${encodeURIComponent(diff)}`}
      replace
    />
  );
}

export default function App() {
  const currentPath = window.location.pathname;
  const hideMarketingNav = [
    "/spoken-english-vsl",
    "/spoken-english-offer",
    "/spoken-english-offer-v2",
    "/spoken-english-offer-legacy",
  ].includes(currentPath);
  return (
    <AuthProvider>
      <Router>
        <FunnelDocumentBoundary>
        <RouteTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Admin login (no Navbar) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            <Route path="/free-live-class" element={<FreeLiveClass />} />
            <Route
              path="/try-spoken-english-gym"
              element={<TrySpokenEnglishGym />}
            />
            <Route
              path="/spoken-english-challenge"
              element={<SpokenEnglishChallenge />}
            />
            <Route path="/memory-challenge" element={<MemoryChallenge />} />
            <Route path="/memory-challenge/video/school" element={<MemoryChallengeVideo key="school" audience="school" />} />
            <Route path="/memory-challenge/video/advanced" element={<MemoryChallengeVideo key="advanced" audience="advanced" />} />
            {/* A/B test: utm_content=parents_b shows variant B on this same path */}
            <Route path="/memory-masterclass/parents" element={<MemoryMasterclassParentsAB />} />
            {/* Unknown memory URLs must not render the English Home fallback. */}
            {["memory-challenge", "memory-masterclass", "memory-retest", "memory-products"].map(prefix => (
              <Route key={prefix} path={`/${prefix}/*`} element={<MemoryRouteNotFound />} />
            ))}
            <Route
              path="/memory-challenge/start"
              element={<MemoryChallengeStart />}
            />
            <Route
              path="/memory-challenge/test"
              element={<MemoryChallengeTest />}
            />
            <Route
              path="/memory-challenge/result"
              element={<MemoryChallengeResult />}
            />
            <Route
              path="/memory-masterclass/thank-you"
              element={<MemoryMasterclassThankYou />}
            />
            <Route path="/vocabulary-vsl" element={<VocabularyVSL />} />
            <Route path="/vocabulary-course" element={<VocabularyCourse />} />
            <Route
              path="/vocabulary-thank-you"
              element={<VocabularyThankYou />}
            />

            {/* All other pages share Navbar */}
            <Route element={<MainLayout />}>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/signin"
                element={<Navigate to="/login" replace />}
              />
              <Route path="/signup" element={<Signup />} />
              <Route path="/smart-signup" element={<SmartSignup />} />
              <Route path="/activation" element={<Activation />} />
              <Route path="/start-practice" element={<StartPractice />} />
              <Route
                path="/quick-start"
                element={
                  <ProtectedRoute>
                    <QuickStart />
                  </ProtectedRoute>
                }
              />
              <Route path="/webinar" element={<Webinar />} />
              <Route path="/join-webinar" element={<JoinWebinar />} />
              <Route
                path="/spoken-english-vsl"
                element={<SpokenEnglishVSL />}
              />
              <Route
                path="/spoken-english-offer"
                element={<SpokenEnglishOfferV2 />}
              />
              <Route
                path="/spoken-english-offer-legacy"
                element={<SpokenEnglishOffer />}
              />
              <Route
                path="/spoken-english-offer-v2"
                element={<SpokenEnglishOfferV2 />}
              />
              <Route
                path="/spoken-english-thank-you"
                element={<SpokenEnglishThankYou />}
              />

              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/attend-webinar" element={<WebinarAttend />} />
              <Route
                path="/webinar-preview-complete"
                element={<WebinarPreviewComplete />}
              />
              <Route path="/paywall" element={<Paywall />} />
              <Route path="/level-check" element={<LevelCheck />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin/curriculum" element={<CurriculumManager />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              {/* 🛡️ UNIFIED LESSON HUB ROUTES */}
              <Route
                path="/b/lessons"
                element={
                  <ProtectedRoute>
                    <LessonList difficulty="basic" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/i/lessons"
                element={
                  <ProtectedRoute>
                    <LessonList difficulty="intermediate" />
                  </ProtectedRoute>
                }
              />
              {/* keep diagnostic as alias → level-check */}
              <Route
                path="/diagnostic"
                element={<Navigate to="/level-check" replace />}
              />
              <Route
                path="/diagnostic/result"
                element={<Navigate to="/level-check" replace />}
              />
              {/* lessons redirect based on fj_track */}
              <Route path="/lessons" element={<LessonsRedirect />} />
              {/* legacy route (IMPORTANT) */}
              <Route path="/lesson/:lessonId" element={<LessonRedirect />} />
              {/* practice entry (redirect only). Actual practice is protected below */}
              <Route
                path="/practice"
                element={<Navigate to="/practice/reorder" replace />}
              />
              {/* Student-protected */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonQuiz />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice"
                element={
                  <ProtectedRoute>
                    <Practice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/typing-quiz"
                element={
                  <ProtectedRoute>
                    <TypingQuiz />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                }
              />
              {/* Admin-protected (unchanged) */}
              <Route
                path="/admin"
                element={
                  <ProtectedAdminRoute>
                    <Admin />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedAdminRoute>
                    <AdminDashboard />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/lessons"
                element={
                  <ProtectedAdminRoute>
                    <AdminLessons />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/lessons/new"
                element={
                  <ProtectedAdminRoute>
                    <AdminLessonCreate />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/lessons/:lessonId"
                element={
                  <ProtectedAdminRoute>
                    <AdminLessonEdit />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/quizzes"
                element={
                  <ProtectedAdminRoute>
                    <AdminQuizzes />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedAdminRoute>
                    <AdminUsers />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <ProtectedAdminRoute>
                    <AdminUserDetail />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/xp"
                element={
                  <ProtectedAdminRoute>
                    <AdminXP />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedAdminRoute>
                    <AdminAnalytics />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/whatsapp-broadcast"
                element={
                  <ProtectedAdminRoute>
                    <WhatsAppBroadcast />
                  </ProtectedAdminRoute>
                }
              />
              {/* 📖 Updated Individual Lesson Routes */}
              <Route
                path="/b/lesson/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonDetail difficulty="beginner" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/i/lesson/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonDetail difficulty="intermediate" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice/:mode"
                element={
                  <ProtectedRoute>
                    <SentencePractice />
                  </ProtectedRoute>
                }
              />
              {/* Fallback */}
              <Route path="*" element={<Home />} />
            </Route>
                  <Route path="/german-live-class" element={<GermanLiveClass />} />
        <Route path="/german-live-class/thank-you" element={<GermanLiveThankYou />} />
      </Routes>
        </Suspense>
        <MemoryMetaConsent />
        </FunnelDocumentBoundary>
      </Router>
    </AuthProvider>
  );
}
// Build Reset: 2026-03-18